import {
  EMAIL_VALIDATION_MESSAGE,
  ERROR_MESSAGE,
  INTERNAL_SERVER_ERROR,
  SEARCH_FORM_URL,
  USER_EXISTS_API,
} from '@/common/constants/constants';
import { useEventbriteUrl } from '@/common/constants/useEventbriteUrl';
import { useCSRFToken } from '@/pages/contextProvider';
import validateEmail from '@/utils/validateEmail';
import { HEADER_NAME } from '@eventbrite/csrf-double-cookie/dist';
import { Button, TextField, Typography, getFormValidationAdornment } from '@eventbrite/marmalade';
import { Stage } from '@eventbrite/webapp-constants';
import { useRouter } from 'next/router';
import React, { FormEvent, useEffect, useState } from 'react';
import { PageTitle } from '../Layout/LeftPane/PageTitle';
import styles from './EmailVerifyForm.module.scss';
import {
  trackAPIError,
  trackAccountFound,
  trackEmailValidationFailure,
  trackFormSubmission,
  trackNoAccountFound,
} from './EmailVerifyHeap';

const stage = process.env.STAGE as Stage;

interface EmailVerifyFormProps {
  onAccountFound: (email: string) => void;
}

export const EmailVerifyForm: React.FunctionComponent<EmailVerifyFormProps> = ({
  onAccountFound,
}: EmailVerifyFormProps) => {
  const router = useRouter();
  const csrfToken = useCSRFToken();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [helperText, setHelperText] = useState('Enter the email address used to order tickets');
  const [isLoading, setIsLoading] = useState(false);
  const baseAuth0Url = useEventbriteUrl('AUTH0_URL');

  useEffect(() => {
    const { email: emailParam } = router.query;
    if (emailParam && typeof emailParam === 'string') {
      setEmail(emailParam);
    }
  }, [router.query]);

  const handleIdontKnowClick = async () => {
    router.push(SEARCH_FORM_URL);
  };

  const getAuth0Url = (emailToCheck: string) => {
    return `${baseAuth0Url}?email=${encodeURIComponent(emailToCheck)}&referrer=%2Fmytickets`;
  };

  const handleEmailCheck = async (emailToCheck: string) => {
    trackFormSubmission();

    if (!validateEmail(emailToCheck)) {
      setError(EMAIL_VALIDATION_MESSAGE);
      setHelperText(EMAIL_VALIDATION_MESSAGE);
      trackEmailValidationFailure();
      return;
    }

    try {
      setIsLoading(true);

      const response = await fetch(`${USER_EXISTS_API}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [HEADER_NAME]: csrfToken,
        },
        body: JSON.stringify({ emailId: emailToCheck }),
      });

      if (!response.ok) {
        throw new Error(INTERNAL_SERVER_ERROR);
      }

      const data = await response.json();

      if (data.exists === undefined) {
        throw new Error(INTERNAL_SERVER_ERROR);
      }

      if (data.exists) {
        trackAccountFound();
        if (data.auth0_enabled) {
          // Construct the Auth0 URL properly
          const auth0RedirectUrl = getAuth0Url(emailToCheck);
          window.open(auth0RedirectUrl, '_self', 'noopener,noreferrer');
        } else {
          onAccountFound(email);
        }
      } else {
        trackNoAccountFound();
        router.push(`${SEARCH_FORM_URL}?email=${encodeURIComponent(emailToCheck)}`);
      }
    } catch (err) {
      setError(ERROR_MESSAGE);
      setHelperText(ERROR_MESSAGE);
      trackAPIError();
    } finally {
      setIsLoading(false);
    }
  };

  const handleOnNextClick = (event: FormEvent) => {
    event.preventDefault();
    handleEmailCheck(email);
  };

  return (
    <main className={styles.leftPaneContent}>
      <PageTitle pageTitle={'Find tickets'} />
      <Typography variant="body-md">Look up your ticket order</Typography>
      <form>
        <TextField
          id="guest-email"
          label="Email address"
          value={email}
          required
          type="text"
          helperText={helperText}
          data-automation="guest-email"
          onChange={(e) => setEmail(e)}
          crossOrigin={undefined}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
          hasError={!!error}
          validationAdornment={getFormValidationAdornment({
            hasError: !!error,
            isValid: validateEmail(email),
          })}
        />
        <div className={styles.buttonWrapperDiv}>
          <Button
            variant="primary"
            className={styles.button}
            size="responsive"
            onClick={handleOnNextClick}
            data-heap-id="email_verify_form_next"
          >
            Next
          </Button>
          <Button
            variant="secondary"
            className={styles.button}
            onClick={handleIdontKnowClick}
            data-heap-id="email_verify_form_i_dont_know"
          >
            I don&apos;t know
          </Button>
        </div>
      </form>
    </main>
  );
};
